// UserStatsTicker.tsx

import "./UserStatsTicker.scss";
import useEmblaCarousel from "embla-carousel-react";
import { useSlice } from "@bridge-applications/provider-kit";
import { useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

const PAUSE_MS = 2000;

export default function UserStatsTicker() {
    const { t } = useTranslation();
    const { state: appSlice } = useSlice("appSlice");

    const [emblaRef, emblaApi] = useEmblaCarousel({
        loop: true,
        align: "start",
        skipSnaps: false,
        watchDrag: false,
        duration: 30,
    });

    const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
        undefined,
    );

    const items = useMemo(
        () => [
            {
                key: "streak",
                num: appSlice.streak?.currentCount ?? 0,
                line1: t("MainPage.userStatsTicker.day"),
                line2: t("MainPage.userStatsTicker.streak"),
            },
            {
                key: "rewards",
                num: appSlice.totalRewardsEarned ?? 0,
                line1: t("MainPage.userStatsTicker.rewards"),
                line2: t("MainPage.userStatsTicker.earned"),
            },
            {
                key: "tasks",
                num: appSlice.totalTasksCompleted ?? 0,
                line1: t("MainPage.userStatsTicker.tasks"),
                line2: t("MainPage.userStatsTicker.completed"),
            },
            {
                key: "streak-2",
                num: appSlice.streak?.currentCount ?? 0,
                line1: t("MainPage.userStatsTicker.day"),
                line2: t("MainPage.userStatsTicker.streak"),
            },
            {
                key: "rewards-2",
                num: appSlice.totalRewardsEarned ?? 0,
                line1: t("MainPage.userStatsTicker.rewards"),
                line2: t("MainPage.userStatsTicker.earned"),
            },
            {
                key: "tasks-2",
                num: appSlice.totalTasksCompleted ?? 0,
                line1: t("MainPage.userStatsTicker.tasks"),
                line2: t("MainPage.userStatsTicker.completed"),
            },
        ],
        [
            appSlice.streak?.currentCount,
            appSlice.totalRewardsEarned,
            appSlice.totalTasksCompleted,
            t,
        ],
    );

    useEffect(() => {
        if (!emblaApi) return;

        const advance = () => {
            emblaApi.scrollNext();
        };

        const onSettle = () => {
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(advance, PAUSE_MS);
        };

        emblaApi.on("settle", onSettle);

        timerRef.current = setTimeout(advance, PAUSE_MS);

        return () => {
            clearTimeout(timerRef.current);
            emblaApi.off("settle", onSettle);
        };
    }, [emblaApi]);

    return (
        <div className="user-stats-ticker" aria-label="Your stats">
            <svg
                width="0"
                height="0"
                aria-hidden="true"
                style={{ position: "absolute" }}
            >
                <defs>
                    <filter id="ust-erode">
                        <feMorphology operator="erode" radius="1" />
                    </filter>
                </defs>
            </svg>
            <div className="embla" ref={emblaRef}>
                <div className="embla__container">
                    {items.map((item) => (
                        <div
                            className="embla__slide user-stats-ticker__chip"
                            key={item.key}
                        >
                            <span
                                className="user-stats-ticker__num"
                                data-text={item.num}
                            >
                                {item.num}
                            </span>
                            <div className="user-stats-ticker__label">
                                <span>{item.line1}</span>
                                <span>{item.line2}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
